import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, ArrowRight, Zap, Truck, Award, CheckCircle, MessageCircle, MapPin, Info } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CARRIER_LOGOS, CARRIER_COLORS } from "@/components/CarrierLogos";

const WHATSAPP_NUMBER = "525563341691";

const QuoteResults = ({ quotes, showActions = false, onSelect = () => {} }) => {
  const [serviceFilter, setServiceFilter] = useState("all");
  const navigate = useNavigate();

  if (!quotes || !quotes.quotes || quotes.quotes.length === 0) {
    return null;
  }

  // Filter quotes by service type
  let filteredQuotes = quotes.quotes;
  if (serviceFilter !== "all") {
    filteredQuotes = filteredQuotes.filter(q => q.service_type === serviceFilter);
  }

  const handleImgError = () => {};

  return (
    <div className="space-y-6" data-testid="quote-results">
      {/* Header */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold font-['Outfit'] flex items-center gap-2">
            <Award className="w-7 h-7 text-violet-600" />
            Mejores precios encontrados
          </h3>
          <p className="text-zinc-500 mt-1">
            {quotes.origin_zip} → {quotes.destination_zip} | {quotes.weight}kg | {quotes.dimensions}
          </p>
        </div>

        {/* Service filter */}
        <div className="flex gap-2 bg-zinc-100 p-1 rounded-xl">
          <Button
            variant={serviceFilter === "all" ? "default" : "ghost"}
            size="sm"
            onClick={() => setServiceFilter("all")}
            className={serviceFilter === "all" ? "bg-violet-600 shadow-md" : ""}
          >
            Todos
          </Button>
          <Button
            variant={serviceFilter === "Express" ? "default" : "ghost"}
            size="sm"
            onClick={() => setServiceFilter("Express")}
            className={serviceFilter === "Express" ? "bg-orange-500 shadow-md" : ""}
          >
            <Zap className="w-4 h-4 mr-1" />
            Express
          </Button>
          <Button
            variant={serviceFilter === "Terrestre" ? "default" : "ghost"}
            size="sm"
            onClick={() => setServiceFilter("Terrestre")}
            className={serviceFilter === "Terrestre" ? "bg-blue-500 shadow-md" : ""}
          >
            <Truck className="w-4 h-4 mr-1" />
            Terrestre
          </Button>
        </div>
      </div>

      {/* Quote cards grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filteredQuotes.map((quote, index) => {
          const logoUrl = CARRIER_LOGOS[quote.carrier];
          const color = CARRIER_COLORS[quote.carrier] || "#333";

          return (
            <Card 
              key={`${quote.carrier}-${quote.service_type}-${index}`}
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 group ${
                quote.is_best_price 
                  ? "ring-2 ring-emerald-500 shadow-xl shadow-emerald-500/20" 
                  : "hover:shadow-violet-500/10"
              }`}
              data-testid={`quote-card-${quote.carrier.toLowerCase().replace(/\s/g, "-")}`}
            >
              {/* Best price badge */}
              {quote.is_best_price && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-center py-2 text-sm font-bold flex items-center justify-center gap-2">
                  <Award className="w-4 h-4" />
                  MEJOR PRECIO
                </div>
              )}
              
              <CardContent className={`p-5 ${quote.is_best_price ? "pt-12" : ""}`}>
                {/* Carrier logo or name */}
                <div 
                  className="w-full h-20 rounded-xl flex items-center justify-center mb-4 p-4 bg-zinc-50"
                >
                  {logoUrl ? (
                    <img 
                      src={logoUrl}
                      alt={`Logo ${quote.carrier}`}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <span className="font-bold text-xl" style={{ color }}>
                      {quote.carrier}
                    </span>
                  )}
                </div>

                {/* Service type badge */}
                <div className="flex items-center justify-between mb-3">
                  <Badge 
                    className={`text-sm px-3 py-1 ${
                      quote.service_type === "Express" 
                        ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0" 
                        : "bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0"
                    }`}
                  >
                    {quote.service_type === "Express" && <Zap className="w-3 h-3 mr-1" />}
                    {quote.service_type === "Terrestre" && <Truck className="w-3 h-3 mr-1" />}
                    {quote.service_type}
                  </Badge>
                  
                  <div className="flex items-center gap-1 text-zinc-500">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{quote.delivery_days}</span>
                  </div>
                </div>

                {/* PUDO info for PuntoPost */}
                {(quote.carrier === "PUNTOPOST" || quote.etp_service_code?.includes("PUDO")) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3" data-testid="pudo-info-badge">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-bold text-amber-800">Servicio punto a punto (PUDO)</p>
                        <p className="text-[11px] text-amber-700 leading-tight mt-0.5">
                          Tú dejas el paquete en un punto PuntoPost cercano y el destinatario lo recoge en otro punto cercano a su ubicación.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Price - prominent with savings */}
                <div className="text-center py-4 border-t border-b border-zinc-100 my-4">
                  <p className="text-sm text-zinc-400 line-through">
                    Precio al público: ${(quote.price * 1.45).toFixed(2)} MXN
                  </p>
                  <p className={`text-4xl font-bold ${
                    quote.is_best_price ? "text-emerald-600" : "text-zinc-900"
                  }`}>
                    ${quote.price.toFixed(2)}
                  </p>
                  <p className="text-sm text-zinc-400">MXN + IVA</p>
                  <div className="mt-2 inline-block bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                    <span className="text-emerald-700 font-semibold text-sm">
                      ¡Ahorras ${(quote.price * 0.45).toFixed(2)} MXN!
                    </span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-zinc-600">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span>Guía incluida</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-zinc-600">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span>Rastreo en tiempo real</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-zinc-600">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span>Seguro incluido</span>
                  </div>
                </div>

                {/* Action buttons */}
                {showActions && (
                  <div className="space-y-2">
                    <Button 
                      className={`w-full py-6 text-base font-semibold ${
                        quote.is_best_price 
                          ? "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg shadow-emerald-500/25" 
                          : "bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                      }`}
                      onClick={() => onSelect(quote)}
                      data-testid={`select-quote-${quote.carrier.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {quote.is_best_price ? "Comprar al mejor precio" : "Continuar online"}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/solicitar-guia", { state: { quote, quoteDetails: quotes } });
                      }}
                      className="block"
                      data-testid={`whatsapp-quote-${quote.carrier.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <Button 
                        variant="outline"
                        className="w-full py-5 text-base font-semibold border-2 border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700"
                        type="button"
                      >
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Comprar por WhatsApp
                      </Button>
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredQuotes.length === 0 && (
        <div className="text-center py-12 text-zinc-500 bg-zinc-50 rounded-2xl">
          <Truck className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
          <p className="text-lg">No hay opciones disponibles con este filtro</p>
        </div>
      )}
    </div>
  );
};

export default QuoteResults;
